import { useState, useEffect } from 'react';
import {
  FileText,
  Shield,
  Clock,
  Award,
  Users,
  CheckCircle,
  TrendingUp,
  Phone,
  Mail,
  ArrowRight,
  ArrowLeft,
  AlertCircle,
  Calculator,
  Star,
  Sparkles,
  User,
  CreditCard,
  MapPin,
  Briefcase,
  IdCard
} from 'lucide-react';
import emailjs from '@emailjs/browser';

// Indian States + UTs
const INDIA_STATES_UTS = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra & Nagar Haveli and Daman & Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
];

const EnquiryForm = ({ loanData }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    panCard: '',
    aadhaarCard: '',
    address: '',
    cityState: '',
    employmentType: '',
    monthlyIncome: '',
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const steps = [
    { title: 'Personal', icon: User },
    { title: 'Employment', icon: Briefcase },
    { title: 'KYC & Secure', icon: Shield },
  ];

  useEffect(() => {
    emailjs.init('iguiOxu1SQS--KIzl');
  }, []);

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'name':
        if (!value.trim()) error = 'Name is required';
        else if (value.trim().length < 2) error = 'Name must be at least 2 characters';
        break;
      case 'email':
        if (!value.trim()) error = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Invalid email format';
        break;
      case 'phone':
        if (!value.trim()) error = 'Phone is required';
        else if (!/^[6-9]\d{9}$/.test(value)) error = 'Enter valid 10-digit number';
        break;
      case 'panCard':
        if (!value.trim()) error = 'PAN is required';
        else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value.toUpperCase()))
          error = 'Enter valid PAN (e.g. ABCDE1234F)';
        break;
      case 'aadhaarCard':
        if (!value.trim()) error = 'Aadhaar is required';
        else if (!/^\d{12}$/.test(value)) error = 'Enter valid 12-digit Aadhaar number';
        break;
      case 'address':
        if (!value.trim()) error = 'Address is required';
        else if (value.trim().length < 10) error = 'Address must be at least 10 characters';
        break;
      case 'cityState':
        if (!value.trim()) error = 'City / State is required';
        break;
      case 'employmentType':
        if (!value.trim()) error = 'Employment type is required';
        break;
      case 'monthlyIncome':
        if (!value.trim()) error = 'Income is required';
        else {
          const num = Number(value.replace(/,/g, ''));
          if (Number.isNaN(num) || num <= 0) error = 'Enter valid income';
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));

    if (name === 'panCard') {
      setFormData((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    } else if (name === 'aadhaarCard' || name === 'phone' || name === 'monthlyIncome') {
      const numericValue = value.replace(/[^0-9]/g, '');
      const maxLen = name === 'phone' ? 10 : name === 'aadhaarCard' ? 12 : 20;
      setFormData((prev) => ({ ...prev, [name]: numericValue.slice(0, maxLen) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleBlur = (name) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, formData[name]) }));
  };

  const validateStep = (stepIndex) => {
    let fields = [];
    if (stepIndex === 0) fields = ['name', 'email', 'phone', 'cityState', 'address'];
    else if (stepIndex === 1) fields = ['employmentType', 'monthlyIncome'];
    else if (stepIndex === 2) fields = ['panCard', 'aadhaarCard'];

    let stepErrors = {};
    let stepTouched = {};
    fields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) stepErrors[field] = error;
      stepTouched[field] = true;
    });

    if (Object.keys(stepErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...stepErrors }));
      setTouched((prev) => ({ ...prev, ...stepTouched }));
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    const serviceID = 'service_o3d745k';
    const templateID = 'template_y1m61ue';

    const submissionDate = new Date().toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
    });

    const formattedIncome = formData.monthlyIncome
      ? `₹${Number(formData.monthlyIncome).toLocaleString('en-IN')}`
      : 'Not Provided';

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      pan_card: formData.panCard.toUpperCase(),
      aadhaar_card: formData.aadhaarCard,
      city: formData.cityState || 'Not Provided',
      employment_type: formData.employmentType || 'To be collected',
      monthly_income: formattedIncome,
      existing_loans: 'To be verified',
      loan_type: loanData?.loanTypeName || 'General Loan',
      loan_amount: `₹${parseFloat(loanData?.amount || 0).toLocaleString('en-IN')}`,
      loan_tenure: `${loanData?.tenure || 0} Years`,
      interest_rate: `${loanData?.rate || 0}% p.a.`,
      monthly_emi: `₹${parseFloat(loanData?.emi || 0).toLocaleString('en-IN')}`,
      total_payment: `₹${parseFloat(loanData?.totalPayment || 0).toLocaleString('en-IN')}`,
      loan_purpose: formData.address,
      preferred_bank: 'No Preference',
      submission_date: submissionDate,
      to_name: 'White & Brooks Loan Solutions Team',
    };

    emailjs.send(serviceID, templateID, templateParams)
      .then((response) => {
        setIsSubmitting(false);
        setIsSubmitted(true);
      })
      .catch((error) => {
        console.error('FAILED', error);
        setIsSubmitting(false);
        alert(`Failed to submit: ${error.text || error.message}`);
      });
  };

  // Input Field Helper
  const InputField = ({ label, name, type = 'text', icon: Icon, placeholder, options }) => {
    const isError = touched[name] && errors[name];
    const borderClass = isError
      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
      : 'border-gray-200 focus:border-blue-500 focus:ring-blue-100';

    return (
      <div className="mb-5">
        <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 mb-2">
          {Icon && <Icon className="w-4 h-4 text-blue-800" />}
          <span>{label} *</span>
        </label>
        {type === 'select' ? (
          <select
            name={name}
            value={formData[name]}
            onChange={handleChange}
            onBlur={() => handleBlur(name)}
            disabled={isSubmitting}
            className={`w-full px-4 py-3 border-2 rounded-lg bg-white focus:outline-none transition-all duration-200 focus:ring-2 disabled:opacity-50 ${borderClass}`}
          >
            <option value="">Select an option</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : type === 'textarea' ? (
          <textarea
            name={name}
            value={formData[name]}
            onChange={handleChange}
            onBlur={() => handleBlur(name)}
            rows="3"
            disabled={isSubmitting}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-all duration-200 focus:ring-2 resize-none disabled:opacity-50 ${borderClass}`}
            placeholder={placeholder}
          />
        ) : (
          <input
            type={type}
            name={name}
            value={formData[name]}
            onChange={handleChange}
            onBlur={() => handleBlur(name)}
            disabled={isSubmitting}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-all duration-200 focus:ring-2 disabled:opacity-50 uppercase-placeholder ${borderClass}`}
            placeholder={placeholder}
          />
        )}
        {isError && (
          <p className="text-red-500 text-xs mt-1 flex items-center space-x-1">
            <AlertCircle className="w-3 h-3" />
            <span>{errors[name]}</span>
          </p>
        )}
      </div>
    );
  };

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up">
        <div className="bg-gradient-to-r from-green-500 to-green-600 p-8 text-white text-center">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 scale-in">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h2 className="text-3xl font-bold mb-2">Application Submitted!</h2>
          <p className="text-green-100">Your loan application has been received successfully</p>
        </div>

        <div className="p-8">
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
              <Clock className="w-5 h-5 text-green-600" />
              <span>What happens next?</span>
            </h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <p className="font-medium">Verification (2-4 hours)</p>
                  <p className="text-gray-600">Our team will verify your KYC details.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <p className="font-medium">Expert Call</p>
                  <p className="text-gray-600">A loan expert will contact you at {formData.phone}</p>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={() => window.location.hash = 'calculator'}
            className="w-full bg-gradient-to-r from-blue-800 to-blue-900 text-white py-3 rounded-lg font-semibold hover:from-blue-900 hover:to-blue-800 transition-all duration-200 flex items-center justify-center space-x-2"
          >
            <Calculator className="w-5 h-5" />
            <span>Calculate Another Loan</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-auto overflow-hidden">
      {/* Header section with Progress Indicator */}
      <div className="bg-gradient-to-b from-gray-50 to-white px-8 pt-8 pb-4 border-b border-gray-100">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Loan Application</h2>

        {/* Step Progress Bar */}
        <div className="flex justify-between items-center relative">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-gray-200 rounded-full z-0"></div>
          <div
            className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-blue-600 rounded-full z-0 transition-all duration-500 ease-in-out"
            style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
          ></div>

          {steps.map((step, idx) => {
            const isActive = idx === currentStep;
            const isCompleted = idx < currentStep;
            const Icon = step.icon;

            return (
              <div key={idx} className="relative z-10 flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 font-bold border-2 
                    ${isActive ? 'bg-blue-600 text-white border-blue-600 scale-110 shadow-md'
                      : isCompleted ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-400 border-gray-300'}`}
                >
                  {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
                </div>
                <span className={`text-xs font-semibold mt-2 absolute top-12 whitespace-nowrap ${isActive ? 'text-blue-600' : 'text-gray-400'}`}>
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="pt-10 sm:pt-12 pb-8 relative">
        <form onSubmit={handleSubmit} className="relative">
          <div className="overflow-hidden w-full">
            <div
              className="flex transition-transform duration-500 ease-in-out w-full"
              style={{ transform: `translateX(-${currentStep * 100}%)` }}
            >
              {/* Step 1: Personal Info */}
              <div className="w-full flex-shrink-0 px-4 sm:px-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="text-blue-600 w-5 h-5" /> Personal Details
                </h3>
                <InputField label="Full Name" name="name" icon={User} placeholder="Enter your full name" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField label="Email Address" name="email" type="email" icon={Mail} placeholder="your@email.com" />
                  <InputField label="Phone Number" name="phone" type="tel" icon={Phone} placeholder="9876543210" />
                </div>
                <InputField label="State / UT" name="cityState" type="select" icon={MapPin} options={INDIA_STATES_UTS} />
                <InputField label="Complete Address" name="address" type="textarea" icon={MapPin} placeholder="Enter full address" />
              </div>

              {/* Step 2: Employment Info */}
              <div className="w-full flex-shrink-0 px-4 sm:px-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Briefcase className="text-blue-600 w-5 h-5" /> Employment Details
                </h3>
                <InputField
                  label="Employment Type"
                  name="employmentType"
                  type="select"
                  icon={Briefcase}
                  options={[
                    'Salaried - Government', 'Salaried - Private', 'Self Employed - Business Owner',
                    'Self Employed - Professional', 'Freelancer / Consultant', 'Retired', 'Other'
                  ]}
                />
                <InputField label="Monthly Income (₹)" name="monthlyIncome" type="tel" icon={Calculator} placeholder="50000" />

                <div className="mt-8 bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 rounded-xl p-5">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <Calculator className="w-5 h-5 text-blue-800" />
                    <span>Loan Status Preview</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm flex-wrap sm:flex-nowrap">
                    <div className="bg-white rounded p-3 shadow-sm">
                      <span className="text-gray-500 block text-xs">Amount</span>
                      <strong className="text-blue-800">₹{parseFloat(loanData?.amount || 0).toLocaleString('en-IN')}</strong>
                    </div>
                    <div className="bg-white rounded p-3 shadow-sm">
                      <span className="text-gray-500 block text-xs">Monthly EMI</span>
                      <strong className="text-green-600">₹{parseFloat(loanData?.emi || 0).toLocaleString('en-IN')}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3: KYC Details */}
              <div className="w-full flex-shrink-0 px-4 sm:px-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="text-blue-600 w-5 h-5" /> Secure KYC Details
                </h3>
                <p className="text-sm text-gray-600 mb-6">Govt. verified documentation is required for instant pre-approvals.</p>

                <InputField label="Aadhaar Card Number" name="aadhaarCard" type="tel" icon={IdCard} placeholder="12 Digit Aadhaar No." />
                <InputField label="PAN Card Number" name="panCard" icon={CreditCard} placeholder="ABCDE1234F" />

                <div className="mt-6 bg-green-50/50 border border-green-200 rounded-xl p-4 flex items-start space-x-3">
                  <Shield className="w-8 h-8 text-green-600 flex-shrink-0" />
                  <div className="text-sm text-gray-700">
                    <p className="font-semibold text-green-900 mb-1">100% Secure Data</p>
                    <p className="text-xs">Your KYC details are bank-grade encrypted and used strictly for identity verification.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-gray-100 flex justify-between items-center px-4 sm:px-8">
            {currentStep > 0 ? (
              <button
                type="button"
                onClick={prevStep}
                className="flex items-center space-x-2 px-6 py-3 rounded-lg text-gray-600 font-semibold hover:bg-gray-100 transition-colors"
                disabled={isSubmitting}
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
            ) : <div className="invisible px-6 py-3">Hidden</div>}

            {currentStep < steps.length - 1 ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex items-center space-x-2 px-8 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all transform hover:scale-105 shadow-md"
              >
                <span>Continue</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center space-x-2 px-8 py-3 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 transition-all transform hover:scale-105 shadow-md disabled:opacity-70 disabled:transform-none"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </span>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Submit & Apply</span>
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
      <style jsx>{`
        .scale-in { animation: scaleIn 0.5s ease-out; }
        .animate-fade-in-up { animation: fadeInUp 0.5s ease-out; }
        @keyframes scaleIn {
          0% { transform: scale(0.5); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes fadeInUp {
          0% { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default function Apply({ loanData }) {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => setIsVisible(true), []);

  if (!loanData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center border-t-4 border-blue-600">
            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
              <Calculator className="w-10 h-10 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Calculate EMI First</h1>
            <p className="text-gray-600 mb-8 max-w-lg mx-auto">
              Please design your loan using our EMI calculator before applying. This step ensures you are aware of the repayment plan.
            </p>
            <a
              href="/#calculator"
              className="inline-flex bg-gradient-to-r from-blue-700 to-blue-600 text-white px-8 py-4 rounded-xl font-bold hover:from-blue-800 hover:to-blue-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg items-center space-x-3"
            >
              <Calculator className="w-5 h-5" />
              <span>Go to EMI Calculator</span>
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  const benefits = [
    { icon: <Clock className="w-6 h-6" />, title: 'Fast Approval', description: 'Priority processing for online applications' },
    { icon: <Shield className="w-6 h-6" />, title: 'Bank-Grade Security', description: 'Your data is encrypted end-to-end' },
    { icon: <Award className="w-6 h-6" />, title: 'Lowest Rates', description: 'We guarantee competitive market rates' },
    { icon: <Users className="w-6 h-6" />, title: 'Expert Assistance', description: 'Get a dedicated advisory expert' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className={`bg-blue-900 text-white relative overflow-hidden transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900 to-blue-800 opacity-90"></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight">Complete Your Application</h1>
          <p className="text-xl text-blue-200 mb-8 max-w-2xl mx-auto font-light">
            You're just a few steps away from securing your financial future. Let's make it official.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 -mt-8 relative z-10 flex flex-col lg:flex-row gap-8">
        <div className="w-full lg:w-1/3 order-2 lg:order-1 space-y-6">
          <div className="bg-white rounded-2xl shadow-lg p-6 border-t-4 border-blue-600">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
              <Award className="w-6 h-6 text-blue-600" />
              <span>Application Benefits</span>
            </h3>
            <div className="space-y-5">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3 group">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 flex-shrink-0">
                    {benefit.icon}
                  </div>
                  <div className="pt-1">
                    <h4 className="font-semibold text-gray-900 text-sm">{benefit.title}</h4>
                    <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-800 to-blue-900 rounded-2xl shadow-lg p-6 text-white text-center">
            <Phone className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
            <h3 className="font-bold text-lg mb-2">Need Help Applying?</h3>
            <p className="text-sm text-blue-100 mb-4 opacity-80">Our experts are available to guide you.</p>
            <a href="tel:1800-123-4567" className="inline-block bg-white text-blue-900 font-bold px-6 py-2 rounded-full hover:bg-yellow-400 transition-colors">
              Call 1800-123-4567
            </a>
          </div>
        </div>

        <div className="w-full lg:w-2/3 order-1 lg:order-2">
          <EnquiryForm loanData={loanData} />
        </div>
      </div>
    </div>
  );
}
